import { useContext } from "react"
import { RestaurantConfigurationContext } from "@/context/RestaurantConfigurationProvider"
import {
  DEFAULT_FULFILMENT,
  orderTypeForFulfilment,
  resolveEffectiveFulfilment,
  type Fulfilment,
} from "@/domain/checkout/fulfilment"
import { useAppSelector } from "@/store/store"

export type EffectiveFulfilment = {
  fulfilment: Fulfilment
  /** `order_type` for preview, cache priming and checkout: Delivery 3, Pickup 2. */
  orderType: number
  pickupEnabled: boolean
  /** The customer's raw selection (may be PICKUP while `fulfilment` has fallen back). */
  selected: Fulfilment
  /**
   * Pickup was chosen but the restaurant no longer offers it. Checkout must not proceed as
   * Delivery on its own: the customer has to choose Delivery explicitly.
   */
  pickupUnavailable: boolean
}

/**
 * Single source for the fulfilment that applies to pricing and checkout.
 * Reads the restaurant context without requiring it, so a subtree rendered outside
 * `RestaurantConfigurationProvider` (or before config loads) fails closed to Delivery.
 */
export function useEffectiveFulfilment(): EffectiveFulfilment {
  const selected = useAppSelector((s) => s.checkoutUx?.fulfilment) ?? DEFAULT_FULFILMENT
  const restaurant = useContext(RestaurantConfigurationContext)
  const pickupEnabled = restaurant?.website_pickup_enabled === true
  const fulfilment = resolveEffectiveFulfilment(selected, pickupEnabled)
  return {
    fulfilment,
    orderType: orderTypeForFulfilment(fulfilment),
    pickupEnabled,
    selected,
    pickupUnavailable: selected === "PICKUP" && !pickupEnabled,
  }
}
